'use client';

import { useState, useEffect, useRef } from 'react';
import { setCookie, hasCookie } from 'cookies-next';
import { motion, AnimatePresence } from 'motion/react';

import useDocumentReadyState from 'app/hooks/useDocumentReadyState';

import Portal from 'app/components/common/Portal';
import { IFile } from '@/app/interfaces/common';
import { defaultProcessText } from 'app/utility/textProcessing';
import { CONSENT_COOKIE_NAME, CONSENT_EVENT } from 'app/utility/cookieConsent';

const TIME_TO_EXPIRES = 7 * 24 * 60 * 60;
const COOKIE_NAME = CONSENT_COOKIE_NAME;

export default function ModalCookie({
  content,
  file
}: {
  content: string | null;
  file?: IFile | null;
}) {
  const [isModalShown, setIsModalShown] = useState(false);
  const readyState = useDocumentReadyState();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (readyState === 'complete' && !hasCookie(COOKIE_NAME)) {
      timerRef.current = setTimeout(() => setIsModalShown(true), 5000);
    }

    return () => {
      clearTimeout(timerRef.current || 0);
    };
  }, [readyState]);

  const handleCookieConsent = () => {
    setCookie(COOKIE_NAME, 'true', {
      path: '/',
      maxAge: TIME_TO_EXPIRES
    });
    setIsModalShown(false);
    // Сообщаем другим компонентам (PromoBanner), что согласие получено.
    window.dispatchEvent(new Event(CONSENT_EVENT));
  };

  if (!content) return null;

  return (
    <Portal>
      <AnimatePresence initial={false}>
        {isModalShown && (
          <motion.div
            data-lenis-prevent
            className="fixed bottom-3 md:bottom-5 lg:bottom-6 right-0 z-30 wrapper-main max-w-full"
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: {
                y: 0,
                opacity: 1,
                transition: { duration: 0.5 }
              },
              closed: { y: '10%', opacity: 0, transition: { duration: 0.5 } }
            }}
          >
            <div className="flex gap-1 min-h-17">
              <div className="bg-graphite-dark text-brown text-p3 md:text-p2 rounded p-3 max-w-70 md:max-w-96 [&_a]:underline flex flex-col gap-1 justify-center">
                <div
                  dangerouslySetInnerHTML={{
                    __html: defaultProcessText(content)
                  }}
                ></div>
                {!!file?.url && (
                  <div>
                    <a href={file.url} target="_blank">
                      {file.title}
                    </a>
                  </div>
                )}
              </div>

              <button
                className="button-default w-15 md:w-20 bg-graphite-dark hover:bg-graphite text-brown border-transparent rounded shrink-0"
                onClick={handleCookieConsent}
              >
                OK
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}
